import React, { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import type { SxProps, Theme } from "@mui/material/styles";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import Button from "@mui/material/Button";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { DIALOG_CLOSE_BUTTON_SX } from "../../../constants/dialogCloseButtonStyles";
import { withoutBackdropClose } from "../../../utils/withoutBackdropClose";
import {
  getAutoTagGroupLabel,
  sortAutoTagGroupsByLabel,
  type RegulationAutoTagGroup,
} from "../../../services/regulationAutoTags";
import {
  buildCategoryTagSections,
  categoryTagItemLabel,
  filterCategoryTagSections,
} from "./categoryTagSections";

export type AutoTagsViewAllDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
  groups: RegulationAutoTagGroup[];
  /**
   * Manual categories/tags tree (V2.1.0 Tags Details).
   * Rendered under the merged/auto groups when present.
   */
  categoryItems?: readonly unknown[];
  /** Regulation ID shown under the modal title (AC2). */
  regulationId?: string | null;
  /** Regulation name/title shown under the modal title (AC2). */
  regulationTitle?: string | null;
}>;

const manualChipSx: SxProps<Theme> = {
  borderRadius: "16px",
  height: "28px",
  backgroundColor: "#f3f4f6",
  border: "1px solid #e5e7eb",
  color: "text.primary",
  fontWeight: 500,
  fontSize: (theme) => theme.typography.body2.fontSize,
  "& .MuiChip-label": { px: 1.5 },
};

const valueChipSx = {
  borderRadius: "16px",
  height: "27px",
  backgroundColor: "#f3f4f6",
  border: "none",
  color: "text.primary",
  fontSize: (theme: { typography: { body2: { fontSize: string } } }) =>
    theme.typography.body2.fontSize,
  "& .MuiChip-label": { px: 1 },
};

const highlightMatch = (text: string, term: string) => {
  if (!term.trim()) return text;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="span" sx={{ color: "primary.main" }}>
        {text.slice(idx, idx + term.length)}
      </Box>
      {text.slice(idx + term.length)}
    </>
  );
};

export function AutoTagsViewAllDialog({
  open,
  onClose,
  groups,
  categoryItems,
  regulationId,
  regulationTitle,
}: AutoTagsViewAllDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [allExpanded, setAllExpanded] = useState(true);
  const [expandedMap, setExpandedMap] = useState<Record<number, boolean>>({});

  const sections = useMemo(
    () =>
      sortAutoTagGroupsByLabel(
        groups.filter(
          (group) => Array.isArray(group.values) && group.values.length > 0,
        ),
      ),
    [groups],
  );

  const filteredSections = useMemo(() => {
    if (!searchTerm.trim()) return sections;
    const term = searchTerm.toLowerCase();
    return sortAutoTagGroupsByLabel(
      sections
        .map((group) => {
          const filteredValues = group.values.filter(
            (value) =>
              value.name.toLowerCase().includes(term) ||
              value.code.toLowerCase().includes(term),
          );
          if (filteredValues.length === 0) return null;
          return { ...group, values: filteredValues };
        })
        .filter((group): group is RegulationAutoTagGroup => group !== null),
    );
  }, [sections, searchTerm]);

  const categorySections = useMemo(
    () => buildCategoryTagSections(categoryItems),
    [categoryItems],
  );

  const filteredCategorySections = useMemo(
    () => filterCategoryTagSections(categorySections, searchTerm),
    [categorySections, searchTerm],
  );

  const headerSubtitle = useMemo(() => {
    const id =
      typeof regulationId === "string" && regulationId.trim()
        ? regulationId.trim()
        : "";
    const name =
      typeof regulationTitle === "string" && regulationTitle.trim()
        ? regulationTitle.trim()
        : "";
    if (id && name) return `${id} ${name}`;
    return id || name || "";
  }, [regulationId, regulationTitle]);

  const handleClose = () => {
    setSearchTerm("");
    setAllExpanded(true);
    setExpandedMap({});
    onClose();
  };

  const isCategoryExpanded = (idx: number) =>
    expandedMap[idx] !== undefined ? expandedMap[idx] : allExpanded;

  const handleCategoryAccordionChange =
    (idx: number) => (_event: React.SyntheticEvent, expanded: boolean) => {
      setExpandedMap((prev) => ({ ...prev, [idx]: expanded }));
    };

  const handleToggleAllCategories = () => {
    const next = !allExpanded;
    setAllExpanded(next);
    const map: Record<number, boolean> = {};
    filteredCategorySections.forEach((_, idx) => {
      map[idx] = next;
    });
    setExpandedMap(map);
  };

  const hasSearch = searchTerm.trim().length > 0;
  const hasAnyTags = sections.length > 0 || categorySections.length > 0;
  const showEmptySearchState =
    hasSearch &&
    filteredSections.length === 0 &&
    filteredCategorySections.length === 0 &&
    hasAnyTags;

  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(() => handleClose())}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: "12px" } } }}
    >
      <DialogTitle
        sx={{
          m: 0,
          px: "26px",
          pt: "22px",
          pb: "14px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="div"
            sx={{
              fontWeight: 700,
              fontSize: "18px",
              color: "text.primary",
              lineHeight: 1.3,
            }}
          >
            Tags Details
          </Typography>
          {headerSubtitle ? (
            <Typography
              component="div"
              data-testid="tags-details-subtitle"
              sx={{
                fontSize: "12px",
                color: "#6A7282",
                mt: "4px",
                lineHeight: 1.4,
                wordBreak: "break-word",
              }}
            >
              {headerSubtitle}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ ...DIALOG_CLOSE_BUTTON_SX, mt: -0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <Box sx={{ px: "26px", pt: 2, pb: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search tags"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          inputProps={{ "aria-label": "Search tags" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#8C8C8C", fontSize: "1.1rem" }} />
              </InputAdornment>
            ),
            endAdornment: hasSearch ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setSearchTerm("")}
                  edge="end"
                >
                  <ClearIcon sx={{ fontSize: "1rem", color: "#99A1AF" }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "3px",
              fontSize: "13px",
              backgroundColor: "#fff",
              height: 36,
              "& fieldset": { borderColor: "#D9D9D9" },
              "&:hover fieldset": { borderColor: "#D9D9D9" },
              "&.Mui-focused fieldset": { borderColor: "#4166EB" },
            },
          }}
        />
      </Box>

      <DialogContent
        sx={{
          px: "26px",
          pt: "6px",
          pb: "22px",
          maxHeight: "600px",
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#e5e7eb",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        }}
      >
        {showEmptySearchState ? (
          <Box
            data-testid="tags-details-empty"
            sx={{ py: 4, textAlign: "center" }}
          >
            <Typography sx={{ fontSize: "13px", color: "#6A7282" }}>
              No tags match
            </Typography>
          </Box>
        ) : !hasAnyTags ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: "13px", color: "#6A7282" }}>
              No tags available.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {filteredSections.map((group, index) => {
              const label = getAutoTagGroupLabel(group);
              const count = group.values.length;

              return (
                <Box
                  key={`${group.concept}-${index}`}
                  data-testid="tags-details-group"
                  sx={{
                    py: "14px",
                    borderBottom:
                      index < filteredSections.length - 1
                        ? "1px solid #F1F2F4"
                        : "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#101828",
                      mb: "10px",
                    }}
                  >
                    {label} ({count})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {group.values.map((value) => (
                      <Chip
                        key={`${group.concept}-${value.code}-${value.name}`}
                        label={highlightMatch(value.name, searchTerm)}
                        sx={valueChipSx}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
            {filteredCategorySections.length > 0 ? (
              <Box data-testid="tags-details-manual-tree">
                {filteredSections.length > 0 ? (
                  <Divider sx={{ mt: "6px", mb: "8px" }} />
                ) : null}
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    onClick={handleToggleAllCategories}
                    startIcon={
                      allExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    }
                    sx={{
                      textTransform: "none",
                      color: "primary.main",
                      fontWeight: 600,
                      fontSize: (theme) => theme.typography.body2.fontSize,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {allExpanded ? "Collapse All" : "Expand All"}
                  </Button>
                </Box>
                {filteredCategorySections.map((section, index) => {
                  const count = section.level3Items.length;
                  const title =
                    count > 0
                      ? `${section.sectionTitle} (${count})`
                      : section.sectionTitle;
                  return (
                    <Accordion
                      key={`${section.sectionTitle}-${index}`}
                      disableGutters
                      elevation={0}
                      expanded={isCategoryExpanded(index)}
                      onChange={handleCategoryAccordionChange(index)}
                      sx={{
                        "&:before": { display: "none" },
                        borderBottom:
                          index < filteredCategorySections.length - 1
                            ? "1px solid #f3f4f6"
                            : "none",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          px: 0,
                          py: 1,
                          minHeight: 0,
                          "& .MuiAccordionSummary-content": {
                            display: "flex",
                            alignItems: "center",
                            my: 0.5,
                          },
                          "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded":
                            {
                              transform: "rotate(180deg)",
                            },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", color: "text.primary" }}
                        >
                          {highlightMatch(title, searchTerm)}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pb: 2, pt: 0 }}>
                        {count > 0 ? (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}
                          >
                            {section.level3Items.map((item, itemIndex) => {
                              const label = categoryTagItemLabel(item);
                              return (
                                <Chip
                                  key={`${section.sectionTitle}-${itemIndex}-${label}`}
                                  label={highlightMatch(label, searchTerm)}
                                  sx={manualChipSx}
                                />
                              );
                            })}
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ color: "#6b7280" }}
                          >
                            No details available.
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            ) : null}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
